import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { Location } from '@angular/common';
import { Lead, PageRequest } from '@models/models';
import { MessageService } from '@services/helpers/message-service/message.service';
import { LeadsDataServiceProvider } from '@services/lead/leads.data.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BrandsDataServiceProvider } from '@services/brands/brands.data.service';

@Component({
	selector: 'recent-leads-block',
	template: require('./recent-leads-block.html'),
	styles: [require('./recent-leads-block.scss')]
})
export class RecentLeadsBlockComponent implements OnInit, OnDestroy {
	@Input() buttonDisabled: boolean;
	@Input() spinningWheelActive: boolean;
	@Output()
	sendEmailModalTrigger: EventEmitter<object> = new EventEmitter<object>();
	@Output()
	editLeadModalTrigger: EventEmitter<object> = new EventEmitter<object>();
	@Output()
	addNewLeadModalTrigger: EventEmitter<void> = new EventEmitter<void>();

	unsubscribe: Subject<void> = new Subject();

	leads: Lead[] = [];
	itemsQuantity: number;
	pagesQuantity: number;
	pageRequest: PageRequest;
	leadForDelete: number;

	readonly PAGE_REQUEST_DEFAULT: PageRequest = {
		paging: {
			pageNumber: 0,
			pageSize: 10
		},
		sortCriteria: {
			fieldName: 'createdAt',
			sortAscending: false
		},
		search: '',
		filter: 'open'
	};
	private allowedFilters: string[];

	constructor(
		private leadsDataService: LeadsDataServiceProvider,
		private messageService: MessageService,
		private brandsDataService: BrandsDataServiceProvider,
		private location: Location
	) {}

	ngOnInit(): void {
		this.pageRequest = this.PAGE_REQUEST_DEFAULT;
		this.configureCommunicationAdapter();
		this.subscribeToBrandsChange();
		this.getData();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	addNew(): void {
		this.addNewLeadModalTrigger.emit();
	}

	updateData(pageRequest: PageRequest): void {
		this.pageRequest = pageRequest;
		this.leadsDataService
			.getLeadsOnPage(pageRequest, false)
			.subscribe(
				(data): void => this.successGetData(data),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	paginationToDefaults(): void {
		this.pageRequest = this.PAGE_REQUEST_DEFAULT;
		delete this.pageRequest.paging.searchToken;
		this.getData();
	}

	showLead(id: number): void {
		this.location.go(`leads/view/${id}`);
	}

	showSendEmailModal(id, primaryContact, secondaryContact): void {
		this.sendEmailModalTrigger.emit({
			leadId: id,
			contact: primaryContact,
			secondaryContact
		});
	}

	showEditLeadModal(index: number): void {
		this.editLeadModalTrigger.emit(this.leads[index]);
	}

	archiveLead(id: number): void {
		this.leadsDataService.archiveLead(id).subscribe(
			(): void => {
				this.messageService.showToaster('s_5', 'lead');
				this.updateModel(true);
				this.updateShootsModel(true);
				this.updateGraphsModel(['leads', 'shoots', 'payments']);
			},
			(error): void => {
				const { errorMessage } = error.error;
				if (errorMessage.includes('Lead contains invoices.')) {
					this.messageService.showToaster('e_4', 'lead');
				} else {
					this.messageService.showDefaultErrorMessage(error);
				}
			}
		);
	}

	showDeleteModal(id: number): void {
		this.leadForDelete = id;
		$('#deleteLeadModal').modal('show');
	}

	isFilterAllowed(filter: string): boolean {
		if (!this.allowedFilters) {
			return false;
		}

		return this.allowedFilters.includes(filter);
	}

	orderByField(filter: string): void {
		if (this.pageRequest.sortCriteria.fieldName === filter) {
			this.pageRequest.sortCriteria.sortAscending = !this.pageRequest
				.sortCriteria.sortAscending;
		} else {
			this.pageRequest.sortCriteria.fieldName = filter;
			this.pageRequest.sortCriteria.sortAscending = false;
		}
		this.pageRequest.paging.pageNumber = 0;

		this.getData();
	}

	updateShootsModel(resetPagination: boolean): void {
		window.snCommunicationAdapter.callMethod(
			'updateShootsModel',
			resetPagination
		);
	}

	updatePaymentsModel(resetPagination: boolean): void {
		window.snCommunicationAdapter.callMethod(
			'updateUpcomingPaymentsModel',
			resetPagination
		);
	}

	private updateModel(resetPagination: boolean): void {
		if (resetPagination) {
			this.paginationToDefaults();
		} else {
			this.getData();
		}
	}

	private getData(): void {
		this.leadsDataService
			.getLeadsOnPage(this.pageRequest, false)
			.subscribe(
				(data): void => this.successGetData(data),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private subscribeToBrandsChange(): void {
		this.brandsDataService.brandsChange
			.pipe(
				takeUntil(this.unsubscribe),
				filter((data): boolean => !!data)
			)
			.subscribe((): void => this.getData());
	}

	private successGetData(data): void {
		this.pageRequest.paging.searchToken = data.searchToken;
		this.itemsQuantity = data ? data.totalSize : 0;
		this.pagesQuantity = Math.ceil(
			this.itemsQuantity / this.pageRequest.paging.pageSize
		);
		this.leads = JSON.parse(JSON.stringify(data.items));
		this.allowedFilters = data.sortingFields;
	}

	private updateGraphsModel = function(tabs) {
		window.snCommunicationAdapter.callMethod('updateGraphsModel', tabs);
	};

	private configureCommunicationAdapter(): void {
		window.snCommunicationAdapter.registerMethod(
			'updateRecentLeadsModel',
			this.updateModel.bind(this)
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/dashboard/recent-leads-block/recent-leads-block.ts